import { useState } from 'react';
import { useBilling } from '../../context/BillingContext';
import { PaymentMethodForm } from './PaymentMethodForm';
import type { PaymentMethod } from '../../types/billing';
import './PaymentMethodList.css';

export function PaymentMethodList() {
  const { paymentMethods, removePaymentMethod, setDefaultPaymentMethod, refresh } = useBilling();
  const [showAddForm, setShowAddForm] = useState(false);
  const [processingId, setProcessingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const getCardIcon = (brand: string) => {
    const icons: Record<string, string> = {
      visa: '💳',
      mastercard: '💳',
      amex: '💳',
      discover: '💳',
    };
    return icons[brand.toLowerCase()] || '💳';
  };

  const handleSetDefault = async (pm: PaymentMethod) => {
    setProcessingId(pm.id);
    setError(null);
    try {
      await setDefaultPaymentMethod(pm.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to set default');
    } finally {
      setProcessingId(null);
    }
  };

  const handleRemove = async (pm: PaymentMethod) => {
    if (!confirm(`Remove card ending in ${pm.last4}?`)) return;

    setProcessingId(pm.id);
    setError(null);
    try {
      const success = await removePaymentMethod(pm.id);
      if (!success) {
        setError('Failed to remove payment method');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove');
    } finally {
      setProcessingId(null);
    }
  };

  const handleAddSuccess = () => {
    setShowAddForm(false);
    refresh();
  };

  return (
    <div className="payment-methods">
      <div className="pm-header">
        <h2>Payment Methods</h2>
        <button className="btn-add" onClick={() => setShowAddForm(true)}>
          + Add Card
        </button>
      </div>

      {error && <div className="pm-error">{error}</div>}

      {paymentMethods.length === 0 ? (
        <div className="pm-empty">
          <div className="pm-empty-icon">💳</div>
          <p>No payment methods saved</p>
          <button className="btn-add-first" onClick={() => setShowAddForm(true)}>
            Add Your First Card
          </button>
        </div>
      ) : (
        <div className="pm-list">
          {paymentMethods.map((pm) => (
            <div
              key={pm.id}
              className={`pm-card ${pm.isDefault ? 'default' : ''} ${pm.isExpired ? 'expired' : ''}`}
            >
              <div className="pm-info">
                <span className="pm-icon">{getCardIcon(pm.brand)}</span>
                <div className="pm-details">
                  <span className="pm-brand">
                    {pm.brand.toUpperCase()} •••• {pm.last4}
                  </span>
                  <span className="pm-expiry">
                    Expires {pm.expMonth.toString().padStart(2, '0')}/{pm.expYear}
                    {pm.isExpired && <span className="expired-badge">Expired</span>}
                  </span>
                </div>
              </div>

              <div className="pm-actions">
                {pm.isDefault ? (
                  <span className="default-badge">Default</span>
                ) : (
                  <button
                    className="btn-set-default"
                    onClick={() => handleSetDefault(pm)}
                    disabled={processingId === pm.id}
                  >
                    {processingId === pm.id ? '...' : 'Set Default'}
                  </button>
                )}
                <button
                  className="btn-remove"
                  onClick={() => handleRemove(pm)}
                  disabled={processingId === pm.id}
                  title="Remove card"
                >
                  &#128465;
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAddForm && (
        <div className="pm-modal-overlay">
          <div className="pm-modal">
            <h3>Add Payment Method</h3>
            <PaymentMethodForm
              onSuccess={handleAddSuccess}
              onCancel={() => setShowAddForm(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
